import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, InstagramLogo } from "@phosphor-icons/react";

export default function InboxPage() {
  const [convs, setConvs] = useState([]);
  const [channel, setChannel] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const q = channel ? `?channel=${channel}` : "";
    const { data } = await api.get(`/conversations${q}`);
    setConvs(data);
    setSelected((prev) => prev || (data.length > 0 ? data[0].contact : null));
  }, [channel]);

  const loadMessages = useCallback(async (contactId) => {
    const { data } = await api.get(`/messages?contact_id=${contactId}`);
    setMessages(data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { if (selected) loadMessages(selected.id); }, [selected, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!selected || !body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", { contact_id: selected.id, body });
      setMessages((prev) => [...prev, data]);
      setBody("");
      if (data.status === "queued") toast.info("Queued — no provider connected yet");
      else if (data.status === "failed") toast.error("Provider rejected the message");
      else toast.success("Sent");
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="h-screen flex flex-col">
        <div className="border-b border-white/10 px-10 py-6 flex items-end justify-between shrink-0">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// live</div>
            <h1 className="font-heading text-4xl tracking-tight mt-1">Unified Inbox</h1>
          </div>
          <div className="flex gap-1 border border-white/10 rounded-sm p-1">
            {[["", "All"], ["whatsapp", "WhatsApp"], ["instagram", "Instagram"]].map(([v, l]) => (
              <button key={v} data-testid={`inbox-filter-${v || "all"}`} onClick={() => { setChannel(v); setSelected(null); }}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-sm transition-colors ${
                  channel === v ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Conversation list */}
          <div className="w-80 border-r border-white/10 overflow-y-auto shrink-0">
            {convs.length === 0 && (
              <div className="p-8 text-center text-white/40 text-sm font-mono">no conversations</div>
            )}
            {convs.map((c) => {
              const isActive = selected?.id === c.contact.id;
              return (
                <button
                  key={c.contact.id}
                  data-testid={`inbox-conv-${c.contact.id}`}
                  onClick={() => setSelected(c.contact)}
                  className={`w-full text-left px-5 py-4 border-b border-white/5 transition-colors ${
                    isActive ? "bg-white/5" : "hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-sm bg-white/10 flex items-center justify-center font-mono text-sm shrink-0">
                      {c.contact.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate flex-1">{c.contact.name}</div>
                        {c.contact.channel === "whatsapp"
                          ? <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" />
                          : <InstagramLogo size={14} weight="fill" className="text-[#E1306C]" />}
                      </div>
                      <div className="text-xs text-white/50 truncate mt-0.5">
                        {c.last_message?.body || "No messages yet"}
                      </div>
                    </div>
                    {c.unread > 0 && (
                      <span className="bg-white text-black text-[10px] rounded-sm h-4 min-w-[16px] px-1 flex items-center justify-center font-mono">
                        {c.unread}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Thread */}
          <div className="flex-1 flex flex-col min-w-0">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-white/40 font-mono text-sm">
                select a conversation
              </div>
            ) : (
              <>
                <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm bg-white/10 flex items-center justify-center font-mono text-sm">
                      {selected.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-medium">{selected.name}</div>
                      <div className="text-xs text-white/40 font-mono">{selected.handle || selected.external_id}</div>
                    </div>
                  </div>
                  <ChannelBadge channel={selected.channel} />
                </div>

                <div ref={scrollRef} data-testid="thread-messages" className="flex-1 overflow-y-auto p-8 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-center text-white/40 font-mono text-sm py-12">no messages — send the first one</div>
                  )}
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-sm border ${
                        m.direction === "out"
                          ? "bg-white text-black border-white"
                          : "bg-[#121212] text-white border-white/10"
                      }`}>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] font-mono mt-1 ${m.direction === "out" ? "text-black/50" : "text-white/40"}`}>
                          {new Date(m.created_at).toLocaleString()} · {m.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={send} className="border-t border-white/10 p-4 flex gap-2 shrink-0">
                  <input
                    data-testid="thread-input"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={`Reply on ${selected.channel === "whatsapp" ? "WhatsApp" : "Instagram"}…`}
                    className="flex-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors"
                  />
                  <button
                    data-testid="thread-send"
                    type="submit"
                    disabled={sending || !body.trim()}
                    className="bg-white text-black px-4 py-2.5 rounded-sm flex items-center gap-2 hover:bg-white/90 transition-colors disabled:opacity-50"
                  >
                    <PaperPlaneTilt size={16} weight="fill" />
                    <span className="text-sm">Send</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
